import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { getLocation, getLocationEmployees } from '../../../services/locationService';
import { createLocationUser, deleteLocationUser } from '../../../services/locationUserService';
import { parseTableParams } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import PersonCell from '../../../components/ui/PersonCell';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function LocationEmployeesTab({ locationId }) {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, recordId: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  // Name of the branch this tab belongs to — the "target" side of the move-
  // confirmation dialog's message (the source side comes from the 409's candidate).
  const [locationName, setLocationName] = useState('');
  const [moveConfirm, setMoveConfirm] = useState({
    isOpen: false, userId: null, name: '', currentLocation: '',
  });
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    getLocation(locationId)
      .then((res) => setLocationName(res.data?.data?.name || ''))
      .catch(() => {});
  }, [locationId]);

  useEffect(() => {
    setFetching(true);
    getLocationEmployees(locationId)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [locationId, queryParams, refreshTrigger]);

  const baseColumns = useMemo(() => [
    {
      name: 'fullName', label: t('employee.fullName'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const name = value || `${row?.firstName || ''} ${row?.lastName || ''}`.trim();
          return <PersonCell name={name} profilePicture={row?.profilePicture} />;
        },
      },
    },
    {
      name: 'email', label: t('employee.email'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
  ], [t, data]);

  const handleRemoveClick = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, recordId: row.locationUserId || row.id, name: row.fullName || row.email });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteLocationUser(deleteConfirm.recordId);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, recordId: null, name: '' });
    setDeleting(false);
  }, [deleteConfirm.recordId]);

  const actionOptions = useMemo(() => [
    { type: 'delete', label: t('location.removeEmployee'), onClick: handleRemoveClick },
  ], [t, handleRemoveClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleAddClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    // createLocationUser expects a userId — prefer e.userId over e.id
    return items.map((e) => ({ id: e.userId || e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
  }, []);

  const handleSave = async (formData) => {
    const { userId } = formData;
    try {
      // Suppress the toast for exactly the one 409 sub-code this component
      // handles itself (opens a dialog for it below). Anything else — the
      // plain locationUser.alreadyExists 409, or a genuine 4xx/5xx — is left
      // to the interceptor and toasts normally (array form of skipErrorToast,
      // see services/api.js). A blanket `true` here would have silenced
      // alreadyExists too, leaving the admin with no feedback at all.
      await createLocationUser({ locationId, userId }, {
        skipErrorToast: ['locationUser.alreadyInAnotherLocation'],
      });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;

      if (status === 409 && message === 'locationUser.alreadyInAnotherLocation') {
        const candidate = err.response?.data?.data?.candidates?.[0];
        setMoveConfirm({
          isOpen: true,
          userId,
          name: candidate?.userFullName || '',
          currentLocation: candidate?.currentLocationName || '',
        });
        // Close the add-employee drawer — the move-confirmation dialog takes over.
        return true;
      }

      // locationUser.alreadyExists (same branch) or anything else — plain
      // error, no dialog; the interceptor's toast already fired for it (not
      // suppressed, see above). No toast here — errors are the interceptor's
      // job, not the component's.
      return false;
    }
  };

  const handleMoveCancel = useCallback(() => {
    setMoveConfirm({ isOpen: false, userId: null, name: '', currentLocation: '' });
  }, []);

  const handleMoveConfirm = useCallback(async () => {
    setMoving(true);
    try {
      // No skipErrorToast this time — a genuine error on the retry surfaces
      // normally, and the success toast ("locationUser.saved") fires too, since
      // the interceptor's success branch is unconditional (no opt-out exists
      // for it, nor is one needed here).
      await createLocationUser({ locationId, userId: moveConfirm.userId, confirmMove: true });
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setMoveConfirm({ isOpen: false, userId: null, name: '', currentLocation: '' });
    setMoving(false);
  }, [locationId, moveConfirm.userId]);

  const schema = useMemo(() => [
    {
      name: 'userId', label: t('location.addEmployee'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchEmployees,
    },
  ], [t, searchEmployees]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('location.removeEmployee')}
        message={t('location.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, recordId: null, name: '' })}
        loading={deleting}
      />

      <ConfirmDialog
        isOpen={moveConfirm.isOpen}
        type="info"
        title={t('location.moveConfirmTitle')}
        message={(
          <>
            {t('location.moveConfirmMessage', {
              name: moveConfirm.name,
              currentLocation: moveConfirm.currentLocation,
              targetLocation: locationName,
            })}
            <br />
            {t('location.moveConfirmNote')}
          </>
        )}
        onConfirm={handleMoveConfirm}
        onCancel={handleMoveCancel}
        loading={moving}
      />

      {useMemo(() => (
        <DataTable
          title={t('location.employees')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          newButtonLabel={t('location.addEmployee')}
          onNewButtonClick={handleAddClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleAddClick,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('location.addEmployee')}
        schema={schema}
        onSave={handleSave}
      />
    </Container>
  );
}
